import { useCallback, useEffect, useState } from 'react';

interface ProgressiveListOptions {
  total: number;
  resetKey: string;
  pageSize?: number;
}

/**
 * How many rows of a long list to render: a first page at once, then a page more each
 * time the sentinel is in view or `showMore` is called. Resets to one page when
 * `resetKey` changes.
 *
 * @param options - `total` rows in the list, `resetKey` that changes with the list (new
 *   binder, new search) and `pageSize` rows per page (default 60).
 * @returns `limit` rows to render, `remaining` rows not rendered, `showMore` to render the
 *   next page, and `sentinelRef` for an element after the last rendered row.
 * @example
 * const { limit, remaining, showMore, sentinelRef } = useProgressiveList({ total, resetKey: id });
 * {items.slice(0, limit).map(renderRow)}
 * {remaining > 0 && <button ref={sentinelRef} onClick={showMore}>Show more</button>}
 */
export function useProgressiveList({ total, resetKey, pageSize = 60 }: ProgressiveListOptions) {
  const [limit, setLimit] = useState(pageSize);
  const [appliedKey, setAppliedKey] = useState(resetKey);
  const [sentinel, setSentinel] = useState<Element | null>(null);

  // Reset during render, so the first paint of a new list is already one page.
  if (appliedKey !== resetKey) {
    setAppliedKey(resetKey);
    setLimit(pageSize);
  }

  const showMore = useCallback(() => setLimit(current => current + pageSize), [pageSize]);

  // Re-observed after every page: an observer only fires when the sentinel crosses into
  // view, and it may still be in view after a page lands.
  useEffect(() => {
    if (!sentinel) return undefined;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry?.isIntersecting) showMore();
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, limit, showMore]);

  const shown = Math.min(limit, total);
  return { limit: shown, remaining: total - shown, showMore, sentinelRef: setSentinel };
}
